'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertTriangle,
  Search as SearchIcon,
  MinusCircle,
  Code2,
  GitPullRequest,
  Loader2,
} from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useCodebaseReposInfinite } from '@/hooks/useCodebaseRepos';
import useOrganizationStore from '@/state/orgState';
import useUserStore from '@/state/userState';

export interface MandatoryRepoItem {
  title: string;
  subtitle?: string;
}

export type DisableFeatureScope =
  | 'enable_auto_comments'
  | 'pr_evaluation';

export interface DisableReposMultiSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Modal title, e.g. "Are you sure you want to disable PR review for your repos?" */
  title: string;
  /** Placeholder for search input, e.g. "Search repositories" */
  searchPlaceholder?: string;
  /** All repo names to show in the list (used when loadFeatureScope is unset) */
  repoNames: string[];
  /**
   * When set, page through DB repos with PR bot installed and keep only those
   * with this feature enabled — so the modal is not capped at the current
   * Code Management table page.
   */
  loadFeatureScope?: DisableFeatureScope;
  /** When set with loadFeatureScope, only keep names also in this set. */
  restrictToNames?: string[];
  /** Repos that currently have the feature enabled (pre-selected for disable) */
  reposWithFeatureEnabled: string[];
  /** Repo that triggered the action (pre-selected) */
  initialSelectedRepo?: string;
  /** When set, only these repos are pre-selected (e.g. single repo when disabling from table row) */
  initialSelectedRepos?: string[];
  /** Optional: repos that must stay enabled (shows warning banner) */
  mandatoryRepos?: MandatoryRepoItem[];
  /** Primary button label, e.g. "Yes, Disable PR review" */
  confirmLabel: string;
  onConfirm: (repoNamesToDisable: string[]) => void;
  /** Optional: show repo icon on the left of each row */
  showRepoIcon?: boolean;
}

const DEFAULT_MANDATORY_REPOS: MandatoryRepoItem[] = [];

export function DisableReposMultiSelectModal({
  open,
  onOpenChange,
  title,
  searchPlaceholder = 'Search Repositories',
  repoNames,
  loadFeatureScope,
  restrictToNames,
  reposWithFeatureEnabled: _reposWithFeatureEnabled,
  initialSelectedRepo,
  initialSelectedRepos,
  mandatoryRepos = DEFAULT_MANDATORY_REPOS,
  confirmLabel,
  onConfirm,
  showRepoIcon = true,
}: DisableReposMultiSelectModalProps) {
  void _reposWithFeatureEnabled;
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const orgUUID = useOrganizationStore((s) => s?.organization)?.OrgUUID ?? '';
  const userUUID = useUserStore((s) => s?.user)?.UserUUID ?? '';

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingFeatureRepos,
  } = useCodebaseReposInfinite(orgUUID, userUUID, {
    pageSize: 50,
    prBotOnly: true,
    enabled: open && Boolean(orgUUID) && Boolean(loadFeatureScope),
  });

  // Drain all pages so the disable list is org-complete, not one table page.
  useEffect(() => {
    if (!open || !loadFeatureScope) return;
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [open, loadFeatureScope, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const featureScopedNames = useMemo(() => {
    if (!loadFeatureScope) return repoNames;
    const names: string[] = [];
    for (const page of data?.pages ?? []) {
      for (const r of page.repositories) {
        if (!r.name) continue;
        const enabled =
          loadFeatureScope === 'enable_auto_comments'
            ? Boolean(r.enableAutoComments)
            : Boolean(r.isPRBotEnabled);
        if (enabled) names.push(r.name);
      }
    }
    if (restrictToNames) {
      const allow = new Set(restrictToNames);
      return names.filter((n) => allow.has(n));
    }
    return names;
  }, [loadFeatureScope, data?.pages, repoNames, restrictToNames]);

  const effectiveRepoNames = loadFeatureScope ? featureScopedNames : repoNames;

  useEffect(() => {
    if (!open) return;
    // Wait until feature-scoped pages finish so selection isn't reset mid-load
    // as more enabled names arrive.
    if (loadFeatureScope && (isLoadingFeatureRepos || hasNextPage)) {
      return;
    }
    const initial =
      initialSelectedRepos && initialSelectedRepos.length > 0
        ? initialSelectedRepos.filter((n) => effectiveRepoNames.includes(n))
        : initialSelectedRepo && effectiveRepoNames.includes(initialSelectedRepo)
          ? [initialSelectedRepo]
          : effectiveRepoNames;
    setSelected(new Set(initial.length > 0 ? initial : effectiveRepoNames));
    setSearch('');
  }, [
    open,
    effectiveRepoNames,
    initialSelectedRepo,
    initialSelectedRepos,
    loadFeatureScope,
    isLoadingFeatureRepos,
    hasNextPage,
  ]);

  const filteredSelectedRepos = useMemo(
    () =>
      Array.from(selected).filter((name) =>
        name.toLowerCase().includes(search.toLowerCase().trim())
      ),
    [selected, search]
  );

  const removeFromSelection = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selected));
    onOpenChange(false);
  };

  const stillLoadingPages =
    Boolean(loadFeatureScope) &&
    (isLoadingFeatureRepos || Boolean(hasNextPage) || isFetchingNextPage);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'bg-ds-surface-white border border-ds-border-subtlest rounded-lg p-0 mx-4 max-w-[520px]',
          'flex flex-col max-h-[85vh]'
        )}
      >
        <DialogHeader className="flex-shrink-0 flex flex-col gap-4 text-left p-4">
          {/* Close button comes from DialogContent; do not add another here */}
          <div className="flex flex-row items-center w-full">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[4.571px] bg-ds-danger-default text-ds-surface-white"
              aria-hidden
            >
              <GitPullRequest
                className="h-[22.857px] w-[22.857px]"
                strokeWidth={1.5}
              />
            </div>
          </div>

          {/* Title text taking full width */}
          <DialogTitle className="text-base font-semibold leading-[1.45] text-ds-text-darker w-full">
            {title}
          </DialogTitle>
        </DialogHeader>

        {/* Separator after header */}
        <div className="h-px w-full bg-ds-border-subtlest shrink-0" />

        <div className="flex flex-1 min-h-0 flex-col gap-3 p-4">
          <div className="relative flex-shrink-0">
            <SearchIcon
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-text-subtle pointer-events-none"
              aria-hidden
            />
            <Input
              type="search"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm"
              aria-label={searchPlaceholder}
            />
          </div>

          <p className="text-xs font-medium text-ds-text-subtle flex-shrink-0 flex items-center gap-2">
            {selected.size}{' '}
            {selected.size === 1 ? 'repository' : 'repositories'} selected
            {stillLoadingPages && (
              <Loader2 className="size-3.5 animate-spin text-ds-text-subtler" />
            )}
          </p>

          <ScrollArea className="flex-1 min-h-[200px] max-h-[340px] rounded-lg border border-ds-border-subtlest">
            <div className="space-y-0">
              {filteredSelectedRepos.length === 0 ? (
                <div className="py-6 text-center text-sm text-ds-text-subtle">
                  {stillLoadingPages
                    ? 'Loading repositories…'
                    : selected.size === 0
                      ? 'No repositories selected. Add repos from the table.'
                      : 'No repositories match your search.'}
                </div>
              ) : (
                filteredSelectedRepos.map((name, index) => (
                  <div key={name}>
                    <div
                      className={cn(
                        'flex items-center gap-2 w-full py-1 px-4 transition-colors',
                        'hover:bg-ds-gray-subtlest/40'
                      )}
                    >
                      {showRepoIcon && (
                        <Code2
                          className="h-6 w-6 shrink-0 text-ds-text-subtle"
                          aria-hidden
                        />
                      )}
                      <span className="flex-1 truncate text-sm font-medium text-ds-text-darker">
                        /{name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFromSelection(name)}
                        className="flex h-6 w-6 shrink-0 items-center justify-center text-ds-text-subtle hover:text-ds-text-darker transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-brand-subtler"
                        aria-label={`Remove ${name} from selection`}
                      >
                        <MinusCircle className="h-6 w-6" strokeWidth={2} />
                      </button>
                    </div>
                    {index < filteredSelectedRepos.length - 1 && (
                      <div className="h-px w-full bg-ds-border-subtlest" />
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {mandatoryRepos.length > 0 && (
            <div
              className={cn(
                'flex-shrink-0 flex items-start gap-2 p-3 rounded-lg border',
                'bg-ds-warning-subtlest border-ds-warning-subtler'
              )}
              role="alert"
            >
              <AlertTriangle className="h-4 w-4 text-ds-warning-default shrink-0 mt-0.5" />
              <div className="text-sm text-ds-warning-darker">
                <p className="font-medium">
                  Disabling certain repos may impact the Entelligence roadmap.
                  Please keep the following enabled.
                </p>
                <ul className="mt-2 space-y-1">
                  {mandatoryRepos.map((item) => (
                    <li key={item.title} className="flex flex-col">
                      <span className="font-medium">{item.title}</span>
                      {item.subtitle && (
                        <span className="text-ds-text-subtle text-xs">
                          {item.subtitle}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Separator before footer */}
        <div className="h-px w-full bg-ds-border-subtlest shrink-0" />

        <div className="flex-shrink-0 p-4">
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[1.5px] border-ds-border-subtlest bg-ds-surface-white text-ds-text-darker hover:bg-ds-gray-subtlest rounded-lg"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={selected.size === 0 || stillLoadingPages}
              className="rounded-lg"
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
